/**
 * Phase E — DealAlert KI/billing UI primitives.
 *
 *   TrialBanner       — top-of-dashboard "X hours left" + upgrade CTA
 *   PaywallOverlay    — full-page lock when trial expired (dashboard/favs)
 *   PaywallModal      — modal-sized lock used by the AI panel
 *   ProUpgradeButton  — shared CTA that posts /billing/create-checkout-session
 *
 * No external icon dep; uses emoji-free unicode symbols + Tailwind only.
 */
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createCheckoutSession } from "../../api/billingApi";
import { useAuth } from "../../auth/AuthContext";

/* -------- Shared CTA -------- */
export function ProUpgradeButton({ children, className = "", "data-testid": testid = "billing-upgrade-button", onError }) {
  const { t } = useTranslation("billing");
  const [busy, setBusy] = useState(false);
  const handleClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { checkout_url } = await createCheckoutSession();
      if (checkout_url) {
        window.location.href = checkout_url;
      }
    } catch (err) {
      setBusy(false);
      const msg =
        err?.response?.data?.error?.message ||
        t("checkoutFailed");
      if (onError) onError(msg);
      else window.alert(msg);
    }
  }, [busy, onError]);

  return (
    <button
      type="button"
      data-testid={testid}
      disabled={busy}
      onClick={handleClick}
      className={
        "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-zinc-950 text-sm font-semibold hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed transition " +
        className
      }
    >
      {busy ? t("redirecting") : children || t("upgradeCta")}
    </button>
  );
}

/* -------- Trial banner -------- */
export function TrialBanner({ trialUntil }) {
  const { t } = useTranslation("billing");
  if (!trialUntil) return null;
  const ms = new Date(trialUntil).getTime() - Date.now();
  if (ms <= 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  const label =
    hours >= 1
      ? t("trialBanner.hours", { hours, minutes: minutes > 0 ? `${minutes} Min.` : "" })
      : t("trialBanner.minutes", { minutes });
  return (
    <div
      data-testid="billing-trial-banner"
      className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 flex items-center justify-between gap-4 flex-wrap"
    >
      <div className="text-sm text-emerald-100">
        <span className="text-emerald-300 mr-1.5">✦</span>
        {label}{" "}
        <span className="text-emerald-200/80">{t("trialBanner.upsell")}</span>
      </div>
      <ProUpgradeButton data-testid="billing-trial-upgrade-button">
        {t("upgradeCta")}
      </ProUpgradeButton>
    </div>
  );
}

/* -------- Locked overlay -------- */
function ProValueList() {
  const { t } = useTranslation("billing");
  const items = [0, 1, 2, 3, 4].map((i) => t(`perks.${i}`));
  return (
    <ul className="space-y-2.5 text-sm text-zinc-300">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-2">
          <span aria-hidden className="text-emerald-300 mt-0.5">✓</span>
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

export function PaywallOverlay({ reason, showNav = false }) {
  const { t } = useTranslation("billing");
  const navigate = useNavigate();
  const { logout } = useAuth();
  const headline =
    reason === "expired_trial_no_sub"
      ? t("overlay.trialExpired")
      : t("overlay.proRequired");
  const onLogout = async () => {
    try {
      await logout();
    } catch {
      /* logout is idempotent */
    }
    navigate("/login");
  };
  return (
    <div
      data-testid="billing-paywall-overlay"
      className="fixed inset-0 z-40 bg-zinc-950/85 backdrop-blur-sm flex items-center justify-center p-6"
    >
      <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-3xl p-8 space-y-6 shadow-2xl">
        <div className="space-y-1.5">
          <div className="text-emerald-300 text-sm tracking-wide uppercase">
            DealAlert Pro
          </div>
          <h2
            data-testid="billing-paywall-headline"
            className="text-2xl text-zinc-50 font-semibold leading-snug"
          >
            {headline}
          </h2>
          <div className="text-sm text-zinc-400">{t("overlay.sub")}</div>
        </div>

        <ProValueList />

        <div className="space-y-2">
          <ProUpgradeButton
            data-testid="billing-paywall-upgrade-button"
            className="w-full justify-center text-base py-3"
          >
            {t("overlay.cta")}
          </ProUpgradeButton>
          <div className="text-[11px] text-zinc-500 text-center">{t("overlay.secure")}</div>
        </div>

        {/* P0 2026-07-29 — expired users keep reaching profile,
            Share & Earn (earn Pro via referrals) and logout. */}
        {showNav && (
          <div className="flex items-center justify-center gap-4 pt-1 text-xs text-zinc-400">
            <button
              type="button"
              data-testid="paywall-nav-profile"
              onClick={() => navigate("/profile")}
              className="hover:text-zinc-200 underline-offset-4 hover:underline"
            >
              {t("overlay.profileLink")}
            </button>
            <button
              type="button"
              data-testid="paywall-nav-share"
              onClick={() => navigate("/share-and-earn")}
              className="hover:text-zinc-200 underline-offset-4 hover:underline"
            >
              {t("overlay.shareLink")}
            </button>
            <button
              type="button"
              data-testid="paywall-nav-logout"
              onClick={onLogout}
              className="hover:text-zinc-200 underline-offset-4 hover:underline"
            >
              {t("overlay.logout")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------- Modal for the AI panel -------- */
export function PaywallModal({ open, onClose, source = "ai_panel" }) {
  const { t } = useTranslation("billing");
  if (!open) return null;
  const headline =
    source === "ai_panel"
      ? t("modal.aiOnly")
      : t("overlay.proRequired");
  return (
    <div
      data-testid="billing-paywall-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="max-w-sm w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-1">
          <div className="text-emerald-300 text-xs tracking-wide uppercase">
            DealAlert Pro
          </div>
          <h3 className="text-lg text-zinc-50 font-semibold">{headline}</h3>
          <p className="text-sm text-zinc-400">{t("modal.sub")}</p>
        </div>
        <ProValueList />
        <div className="flex flex-col gap-2">
          <ProUpgradeButton
            data-testid="billing-paywall-modal-upgrade"
            className="w-full justify-center"
          >
            {t("modal.cta")}
          </ProUpgradeButton>
          <button
            type="button"
            data-testid="billing-paywall-modal-dismiss"
            onClick={onClose}
            className="text-xs text-zinc-400 hover:text-zinc-200 underline-offset-4 hover:underline self-center"
          >
            {t("modal.later")}
          </button>
        </div>
      </div>
    </div>
  );
}
